import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useGetConsignmentByIdQuery,
  useLazyDownloadConsignmentAttachmentQuery,
  downloadAttachment,
  type AttachmentSummary,
  type LocalAttachment,
  type AttachmentType
} from '@/entities/consignments';
import { useProjectSiteParams, validateFile, generateTempId } from '@/shared/lib';

import type { AttachmentsData } from './types';

interface UseConsignmentAttachmentsParams {
  consignmentId?: string;
}

export const useConsignmentAttachments = ({ consignmentId = '' }: UseConsignmentAttachmentsParams): AttachmentsData => {
  const { projectId, siteId } = useProjectSiteParams();
  const [localAttachments, setLocalAttachments] = useState<LocalAttachment[]>([]);

  const {
    data: consignment,
    isLoading,
    refetch
  } = useGetConsignmentByIdQuery(
    { id: consignmentId, projectId, siteId },
    { skip: !consignmentId || !projectId || !siteId }
  );

  const [downloadAttachmentQuery] = useLazyDownloadConsignmentAttachmentQuery();

  const attachments = useMemo(() => {
    const serverAttachments = consignment?.attachments || [];
    return [...serverAttachments, ...localAttachments];
  }, [consignment?.attachments, localAttachments]);

  const addLocalAttachment = useCallback((file: File, attachmentType: AttachmentType, comment?: string) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      return;
    }

    const tempId = generateTempId();
    const newAttachment: LocalAttachment = {
      file,
      fileName: file.name,
      attachmentType,
      comment,
      tempId
    };

    setLocalAttachments((prev) => [...prev, newAttachment]);
    toast.success('Attachment added', {
      description: 'The file will be uploaded when you save the consignment'
    });
  }, []);

  const removeLocalAttachment = useCallback((tempId: string) => {
    setLocalAttachments((prev) => prev.filter((att) => att.tempId !== tempId));
    toast.info('Attachment removed');
  }, []);

  const updateLocalAttachment = useCallback((tempId: string, updates: Partial<LocalAttachment>) => {
    setLocalAttachments((prev) => prev.map((att) => (att.tempId === tempId ? { ...att, ...updates } : att)));
  }, []);

  const clearLocalAttachments = useCallback(() => {
    setLocalAttachments([]);
  }, []);

  const getAttachmentFiles = useCallback(() => {
    if (localAttachments.length === 0) {
      return null;
    }

    return {
      files: localAttachments.map((a) => a.file),
      attachmentType: localAttachments[0].attachmentType,
      comment:
        localAttachments
          .map((a) => a.comment)
          .filter(Boolean)
          .join('; ') || undefined
    };
  }, [localAttachments]);

  const handleDownload = useCallback(
    async (attachment: AttachmentSummary | LocalAttachment) => {
      try {
        const downloadFn = async (attachmentId: number) => {
          if (!consignmentId) {
            throw new Error('Cannot download server attachment without consignment ID');
          }
          const result = await downloadAttachmentQuery({
            id: consignmentId,
            attachmentId,
            projectId,
            siteId
          }).unwrap();
          return result;
        };

        await downloadAttachment(attachment, downloadFn);
      } catch (error) {
        console.error('Failed to download attachment:', error);
        throw error;
      }
    },
    [consignmentId, projectId, siteId, downloadAttachmentQuery]
  );

  const isLocalAttachment = useCallback(
    (attachment: AttachmentSummary | LocalAttachment): attachment is LocalAttachment => {
      return 'tempId' in attachment && !!attachment.tempId;
    },
    []
  );

  const handleRemove = useCallback(
    (attachment: AttachmentSummary | LocalAttachment) => {
      if (isLocalAttachment(attachment) && attachment.tempId) {
        removeLocalAttachment(attachment.tempId);
      }
    },
    [isLocalAttachment, removeLocalAttachment]
  );

  return {
    // Display data
    attachments,
    serverAttachments: consignment?.attachments || [],
    localAttachments,
    isLoading,

    // Actions
    handleDownload,
    handleRemove,
    addLocalAttachment,
    removeLocalAttachment,
    updateLocalAttachment,
    clearLocalAttachments,

    // Utilities
    getAttachmentFiles,
    isLocalAttachment,
    refetch
  };
};
